import React from "react";
import {
    Sparkles,
    Sunrise,
    Sun,
    Sunset,
    Moon,
    Plus,
    ChevronRight,
} from "lucide-react";
import { ArrowRight } from "lucide-react";

const dayPlanIcons = {
    Morning: Sunrise,
    Afternoon: Sun,
    Evening: Sunset,
    Night: Moon,
};

const API_URL = "https://api.gosehari.com";
const getDayPlanImageUrl = (imagePath) => (
    imagePath?.startsWith("http") ? imagePath : `${API_URL}${imagePath || ""}`
);

export default function AiRecommendation({ dayPlan = [], frequentlyBookedTogether = [], exploreMoreDestinations = [] }) {
    return (
        <section className="mt-4 mb-4 rounded-[28px] border border-[#c4e7cf] bg-gradient-to-r from-[#e3f7ea] via-[#ffffff] to-[#dcf4e5] px-3 py-4 shadow-[0_10px_30px_rgba(16,24,40,0.10)]">

            {/* Header */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
                <div>
                    <h2 className="flex items-center gap-2 font-bold text-green-700">
                        Plan More, Experience More!
                        <Sparkles className="text-yellow-500" size={20} />
                    </h2>

                    <p className="mt-1 text-gray-500 text-sm">
                        AI-powered suggestions to make your Go Sehari trip unforgettable.
                    </p>
                </div>

                <button className="rounded-xl border bg-white px-5 py-2 text-green-700 font-semibold hover:bg-green-50">
                    Save More Together
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1.05fr_0.95fr] gap-3">
                {/* Complete Your Day */}
                <div className="h-full rounded-[22px] border border-[#e8edf2] bg-white p-6 shadow-[0_4px_18px_rgba(0,0,0,0.05)] hover:shadow-lg transition-all duration-300">
                    <div className="flex items-start gap-3 mb-6">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-700 text-sm font-semibold text-white">
                            1
                        </div>

                        <div>
                            <h3 className="text-lg font-bold text-gray-900">
                                Complete Your Day
                            </h3>
                            <p className="text-[13px] text-gray-500">
                                Recommended itinerary
                            </p>
                        </div>
                    </div>



                    <div className="grid grid-cols-4 gap-2">
                        {dayPlan.map((item, index) => {
                            const Icon = dayPlanIcons[item.time_period] || Sun;

                            return (
                                <div key={item.id || index} className="group">

                                    {/* Time */}
                                    <div className="mb-3 flex items-center justify-center gap-1 text-[12px] font-bold text-gray-600">
                                        <Icon size={16} />
                                        {item.time_period}
                                    </div>

                                    {/* Image */}
                                    <div className="relative overflow-visible">

                                        <img
                                            src={getDayPlanImageUrl(item.image_url)}
                                            alt={item.period_title}
                                            className="h-[180px] w-full rounded-xl object-cover transition duration-300 group-hover:scale-110"
                                        />

                                        {/* Arrow */}
                                        {index !== dayPlan.length - 1 && (
                                            <div className="absolute top-1/2 -right-5 -translate-y-1/2 z-20">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white shadow-lg">
                                                    <ArrowRight
                                                        size={15}
                                                        strokeWidth={2.5}
                                                        className="text-green-600"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                    {/* Title */}
                                    <p className="mt-3 text-center text-[13px] font-bold text-gray-700">
                                        {item.period_title}
                                    </p>

                                    {/* Description */}
                                    <p className="mt-1 text-center text-[12px] leading-4 text-gray-500">
                                        {item.description}
                                    </p>

                                </div>
                            );
                        })}
                    </div>
                </div>














                {/* Combo */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">

                    {/* Header */}
                    <div className="flex items-start gap-3 mb-8">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-700 text-sm font-bold text-white">
                            2
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                Frequently Booked Together
                            </h3>

                            <p className="text-sm text-gray-500">
                                Popular combo packages
                            </p>
                        </div>
                    </div>

                    {/* Combo Items */}
                    <div className="mt-6 flex gap-3 items-start justify-between">
                        {frequentlyBookedTogether.map((item, index) => (
                            <div key={index} className="relative w-[145px]">

                                {/* Image */}
                                <div className="relative">
                                    <img
                                        src={getDayPlanImageUrl(item.image_url)}
                                        alt={item.title}
                                        className="h-[100px] w-full rounded-xl object-cover"
                                    />

                                    {/* Floating Plus */}
                                    {index !== frequentlyBookedTogether.length - 1 && (
                                        <div className="absolute top-1/2 -right-6 -translate-y-1/2 z-20">
                                            <div className="flex h-8 w-8 items-center justify-center rounded-full border border-gray-200 bg-white shadow-md">
                                                <Plus
                                                    size={16}
                                                    strokeWidth={2.5}
                                                    className="text-green-600"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Title */}
                                <h4 className="mt-3 text-center text-[15px] font-semibold leading-5 text-gray-800">
                                    {item.title}
                                </h4>

                                {/* Price */}
                                <p className="mt-1 text-center">
                                    <span className="text-xl font-bold text-green-600">
                                        {item.price}
                                    </span>
                                    <span className="ml-1 text-sm font-normal text-gray-500">
                                        / person
                                    </span>
                                </p>
                            </div>
                        ))}
                    </div>

                    {/* Bottom CTA */}
                    <div className="mt-8 flex gap-2 items-center justify-between rounded-2xl bg-[#EFFFF4] px-4 py-2">

                        <div>
                            <p className="mt-1 text-center">
                                <span className="text-sm font-bold text-green-600">
                                    Save up to 15%
                                </span>
                                <span className="ml-1 text-sm font-normal text-gray-500">
                                    on combo bookings
                                </span>
                            </p>


                        </div>

                        <button className="rounded-lg border text-sm border-gray-200 bg-white px-7 py-2 font-semibold shadow-sm transition hover:bg-gray-50">
                            View All Combos
                        </button>

                    </div>

                </div>

                {/* Explore */}
                <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                    <div className="flex items-start gap-3 mb-8">
                        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-violet-700 text-sm font-bold text-white">
                            3
                        </div>

                        <div>
                            <h3 className="text-xl font-bold text-gray-900">
                                Explore More Destinations
                            </h3>

                            <p className="text-sm text-gray-500">
                                Trending locations
                            </p>
                        </div>
                    </div>




                    <div className="space-y-4">
                        {exploreMoreDestinations.map((item, index) => (
                            <div
                                key={index}
                                className="flex cursor-pointer items-center rounded-xl border p-0 transition hover:bg-green-50"
                            >
                                <img
                                    src={getDayPlanImageUrl(item.image_url)}
                                    alt=""
                                    className="h-14 w-20 rounded-lg object-cover"
                                />

                                <div className="ml-3 flex-1">
                                    <h4 className="font-semibold">
                                        {item.name}
                                    </h4>

                                    <p className="text-xs text-gray-500">
                                        {item.experience_count}
                                    </p>
                                </div>

                                <ChevronRight size={20} className="text-gray-400" />
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </section>
    );
}
